import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { FEATURE } from '~/common/enums/feature.enum';
import { updateAnalyticsUser } from '~/domains/analytics';
import useCurrentLocation from '~/domains/contest/domains/services/hooks/useCurrentLocation';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { useLocation } from '~/hooks/useLocation';
import { GeoComplyReasonCode } from '~/services/interceptors/set-location.interceptor';
import { SocureKycStatus } from '~/services/users/types';
import GeoComplySingleton, {
  CheckRequestStatus,
  LocationCheckErrorType,
  type CheckRequestError,
} from '~/utils/geocomply';
import { PermissionStatusState, getGeoPermissions } from '~/utils/geoPermissions';
import { isEoBrand } from '~/utils/isEoBrand';
import isWebView from '~/utils/isWebView';
import { locationStorage, type LocationCountryName } from '~/utils/locationStorage';

import { LocationProviderQueryKeys } from './query';
import { UserContext } from './UserProvider';

const AllowedLocations: LocationCountryName[] = ['united states', 'canada'];
const DEFAULT_LOCATION_COUNTRY_NAME: LocationCountryName = 'united states';

export const LocationContext = createContext<{
  locationCountry?: string;
  locationCountryNameSafe?: LocationCountryName;
  locationErrors?: CheckRequestError[];
  locationState?: string;
  locationStatus?: CheckRequestStatus;
  initialLocationLoaded: boolean;
  verifyingLocation: boolean;
  allowedLocation: boolean;
  locationError?: CheckRequestError;
  currentLocation?: string;
  currentCountry?: string;
  geoPermissionStatus?: PermissionState;
  isLoading?: boolean;
  isGeoComplyEnabled: boolean;
  setLocationCountryNameOverride: (country: LocationCountryName) => void;
  refetchGeoPermissionStatus: () => void;
  refetchLocationToken: (forceRefetch?: boolean) => Promise<unknown>;
}>({
  locationCountry: undefined,
  locationErrors: undefined,
  locationState: undefined,
  locationStatus: undefined,
  initialLocationLoaded: false,
  verifyingLocation: false,
  allowedLocation: false,
  locationError: undefined,
  currentLocation: undefined,
  currentCountry: undefined,
  geoPermissionStatus: undefined,
  refetchGeoPermissionStatus: () => {},
  refetchLocationToken: () => Promise.resolve(),
  setLocationCountryNameOverride: () => {},
  isGeoComplyEnabled: false,
});

function LocationProvider({ children }: { children?: ReactNode }) {
  const { user, userDetails } = useContext(UserContext);
  const [geoPermissionStatus, setGeoPermissionStatus] = useState<PermissionState>();
  const isLocationCheckOnLoginEnabled = useIsFeatureEnabled(FEATURE.ENABLE_LOCATION_CHECK_ON_LOGIN);
  const [locationCountryNameOverride, setLocationCountryNameOverride] =
    useState<LocationCountryName>();

  const [cachedLocationData, setCachedLocationData] = useState(
    locationStorage.getCachedLocationData
  );
  const lastCachedLocationRef = useRef<string | null>(null);

  // Step 1. We only enable GeoComply location checks for users who are:
  // - not using Unify Auth (EO brands)
  // - not using a webview
  // - are KYC verified
  const isEOorWebView = isWebView() || isEoBrand();
  const isGeoComplyEnabled = useMemo(
    // @TODO: Use KYCStatus from KYCProvider
    () => !!user && userDetails?.verificationStatus === SocureKycStatus.ACCEPT && !isEOorWebView,
    [user, userDetails?.verificationStatus, isEOorWebView]
  );

  // Step 2: Init location status closures with caching
  const { locationStatus, locationState, locationCountry, locationErrors, locationError } =
    useLocation();

  useEffect(() => {
    if (isGeoComplyEnabled && locationCountry) {
      const locationDataKey = `${locationCountry}-${locationState}-${locationStatus}-${JSON.stringify(locationErrors)}`;

      // Only update if the location data has actually changed
      if (lastCachedLocationRef.current !== locationDataKey) {
        const locationData = {
          locationCountry,
          locationState,
          locationStatus,
          locationErrors,
        };
        locationStorage.setCachedLocationData(locationData, 'geocomply');

        // Update state with a stable timestamp to prevent infinite loops
        const newCachedData = {
          ...locationData,
          source: 'geocomply' as const,
          timestamp: Date.now(),
        };
        setCachedLocationData(newCachedData);
        lastCachedLocationRef.current = locationDataKey;
      }
    }
  }, [isGeoComplyEnabled, locationCountry, locationState, locationStatus, locationErrors]);

  // Step 3: Log in into the Web Platform
  const { isSuccess: isGeoComplyReady } = useQuery({
    queryKey: LocationProviderQueryKeys.GEOCOMPLY_INIT(user?.id),
    queryFn: async () => {
      await GeoComplySingleton.init(user?.id);
      await GeoComplySingleton.startIpChangeDetection({
        reasonCode: GeoComplyReasonCode.IP_CHANGE,
      });
      return null;
    },
    staleTime: Infinity,
    cacheTime: 0,
    enabled: !!user?.id,
  });

  // Step 4: Ensure any token unless disabled by the ENABLE_LOCATION_CHECK_ON_LOGIN flag
  // The business logic is "fetch location token after login"
  // If user just logged in, there is no locationToken in localStorage so this will fetch a new one
  // If user just refreshed a browser, the previous token is saved in localStorage
  // so the fetch won't trigger
  // Added 6/20: If the users geoPermissionStatus updates, we will refetch location token
  const {
    refetch: refetchLocationTokenQuery,
    isInitialLoading: isLocationTokenLoading,
    isFetching: isLocationFetching,
  } = useQuery({
    queryKey: LocationProviderQueryKeys.LOCATION_TOKEN(user?.id, geoPermissionStatus),
    queryFn: async () => {
      await GeoComplySingleton.getValidLocationToken(
        0,
        geoPermissionStatus === PermissionStatusState.GRANTED,
        { reasonCode: GeoComplyReasonCode.LOGIN }
      );

      return null;
    },
    staleTime: Infinity,
    cacheTime: 0,
    enabled: isGeoComplyReady && isLocationCheckOnLoginEnabled,
  });

  const refetchLocationToken = useCallback(
    async (forceRefetch = false) => {
      if (forceRefetch) {
        await GeoComplySingleton.getValidLocationToken(0, true, {
          reasonCode: GeoComplyReasonCode.LOGIN,
        });
        return null;
      }

      return refetchLocationTokenQuery();
    },
    [refetchLocationTokenQuery]
  );

  // Step 5: Use navigator permissions obtain geolocation permission
  // Do not fire location prompt if we're in a webview
  const { refetch: refetchGeoPermissionStatus, isInitialLoading: isGeoPermissionLoading } =
    // eslint-disable-next-line local-rules/require-query-stale-time
    useQuery({
      queryKey: LocationProviderQueryKeys.GEO_PERMISSION_STATUS,
      queryFn: async () =>
        getGeoPermissions(setGeoPermissionStatus as (status: PermissionStatusState) => void),
      enabled: !!navigator?.permissions && isGeoComplyEnabled,
    });

  // Anonymous user location detection via contest-service using GeoIP headers
  const { data: anonUserCurrentLocation, isInitialLoading: anonUserCurrentLocationIsLoading } =
    useCurrentLocation({
      enabled: !isGeoComplyEnabled,
    });

  useEffect(() => {
    if (!isGeoComplyEnabled && anonUserCurrentLocation?.country) {
      locationStorage.setCachedGeoIPLocation(anonUserCurrentLocation.country);
    }
  }, [isGeoComplyEnabled, anonUserCurrentLocation?.country]);

  const verifyingLocation = isLocationTokenLoading || isLocationFetching;
  const initialLocationLoaded = !!locationStatus || !!locationError || !!locationErrors?.length;
  const allowedLocation = locationStatus === CheckRequestStatus.ALLOWED && !locationErrors?.length;
  const currentLocation =
    locationState && locationCountry ? `${locationState}, ${locationCountry}` : undefined;
  const currentCountry = locationCountry ?? anonUserCurrentLocation?.country;

  useEffect(() => {
    if (
      !isGeoComplyEnabled ||
      locationError?.type !== LocationCheckErrorType.PERMISSION_REQUIRED ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchGeoPermissionStatus();
        refetchLocationToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isGeoComplyEnabled, locationError?.type, refetchGeoPermissionStatus, refetchLocationToken]);

  // @ts-expect-error TODO Fix later
  const locationCountryName: LocationCountryName = useMemo(() => {
    if (isGeoComplyEnabled && locationCountry) {
      return locationCountry.toLowerCase();
    }
    if (!isGeoComplyEnabled && anonUserCurrentLocation?.country) {
      return anonUserCurrentLocation.country.toLowerCase();
    }

    if (cachedLocationData?.locationCountry) {
      return cachedLocationData.locationCountry.toLowerCase();
    }

    const cachedGeoIP = locationStorage.getCachedGeoIPLocation();
    if (cachedGeoIP?.country) {
      return cachedGeoIP.country.toLowerCase();
    }

    return DEFAULT_LOCATION_COUNTRY_NAME;
  }, [
    anonUserCurrentLocation?.country,
    locationCountry,
    isGeoComplyEnabled,
    cachedLocationData?.locationCountry,
  ]);

  // Step 6: Report the location status, location and country changes to Segment
  useEffect(() => {
    if (user) {
      updateAnalyticsUser({
        id: user.id,
        geo_status: locationStatus || 'NO GEO STATUS SET',
        geo_detected_state: locationState,
        geo_detected_country: locationCountry,
      });
    }
  }, [user, locationStatus, locationState, locationCountry]);

  // Step 7: Return memoised location props to the provider
  // `locationCountryNameSafe` is used for displaying filtered content based on user's location
  // If the location is not in the allowed list, we default to 'united states'
  // and show e.g. homepage content for as if the user is in the US
  const locationCountryNameSafe: LocationCountryName = useMemo(() => {
    if (locationCountryNameOverride) {
      return locationCountryNameOverride;
    }

    if (!AllowedLocations.includes(locationCountryName)) {
      return DEFAULT_LOCATION_COUNTRY_NAME;
    }

    return locationCountryName;
  }, [locationCountryName, locationCountryNameOverride]);

  const value = useMemo(
    () => ({
      locationErrors,
      locationError,
      locationStatus,
      locationState,
      locationCountry: locationCountryNameOverride ?? locationCountryName,
      locationCountryNameSafe,
      initialLocationLoaded,
      verifyingLocation,
      allowedLocation,
      currentLocation,
      currentCountry,
      isLoading: isGeoComplyEnabled
        ? isGeoPermissionLoading || verifyingLocation
        : anonUserCurrentLocationIsLoading &&
          !cachedLocationData &&
          !locationStorage.getCachedGeoIPLocation(),
      isGeoComplyEnabled,
      setLocationCountryNameOverride,
      geoPermissionStatus,
      refetchGeoPermissionStatus,
      refetchLocationToken,
    }),
    [
      locationErrors,
      locationError,
      locationStatus,
      locationState,
      locationCountryNameOverride,
      locationCountryName,
      locationCountryNameSafe,
      initialLocationLoaded,
      verifyingLocation,
      allowedLocation,
      currentLocation,
      currentCountry,
      isGeoComplyEnabled,
      isGeoPermissionLoading,
      anonUserCurrentLocationIsLoading,
      cachedLocationData,
      geoPermissionStatus,
      refetchGeoPermissionStatus,
      refetchLocationToken,
    ]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export default LocationProvider;
